import {
  RefObject, useEffect, useLayoutEffect, useRef, useState,
} from 'react';

import { PageMeta, paginateByHeights, withPageNumbers } from './pagination';

type Args = {
  // 計測対象ブロック数。blockRefs にこの数だけ要素が積まれてから計測する。
  blockCount: number;
  // オフスクリーン計測コンテナ。
  containerRef: RefObject<HTMLElement>;
  // 各ブロック（= 計測時は個別 <table>）の実 DOM 参照配列。
  blockRefs: RefObject<Array<HTMLElement | null>>;
  // フルサマリヘッダーの DOM 参照（高さを利用可能高から差し引く）。
  headerRef: RefObject<HTMLElement>;
  // 列見出し(thead)の DOM 参照。確定ページにも thead が 1 回出るため available から差し引く。
  // ブロック高さは <tr>/<tbody> を直接測るため thead 分の補正は不要。
  theadRef: RefObject<HTMLElement>;
  // フッタの DOM 参照。確定ページにもフッタが 1 回出るため実測して available から差し引く。
  footerRef: RefObject<HTMLElement>;
  // ページ高（px）。A4横 210mm を px 換算した値（DPI 依存。呼び出し側で定数化）。
  pageHeightPx: number;
  // .page の上下 padding 合計の px 換算（呼び出し側で定数化）。available から差し引く。
  pageVerticalPaddingPx: number;
  // データが変わったら再計測するためのキー。
  depsKey: unknown;
  // keepWithNext[i]===true のブロックを次ブロックと同ページに留める（paginateByHeights に転送）。
  keepWithNext?: boolean[];
};

// オフスクリーンに描画済みの全ブロックを 1 度だけ実測し、
// paginateByHeights → withPageNumbers でページ構成を 1 回の setState で確定する。
// 再計測ループ防止のため measuredFor ガードで同一 depsKey は再計測しない。
// ウェブフォント未ロードによる高さ誤差を避けるため document.fonts.ready 解決後に計測する。
export function useMeasuredPages(args: Args): PageMeta[] | null {
  const {
    blockCount, containerRef, blockRefs, headerRef, theadRef,
    footerRef, pageHeightPx, pageVerticalPaddingPx, depsKey, keepWithNext,
  } = args;

  const [pages, setPages] = useState<PageMeta[] | null>(null);
  // depsKey 変化時に pages を同期リセットして表示フラッシュ（古いページ構成の一瞬表示）を防ぐ。
  // レンダ中の派生 state リセット（React 公式パターン）。useEffect でやると確定直後に打ち消され
  // 依存安定で再実行されず永久ブランクになるため、必ずレンダ中に行う。
  // マウント時は prevDepsKey === depsKey なのでリセットされない（現行の正常動作を壊さない）。
  const [prevDepsKey, setPrevDepsKey] = useState<unknown>(depsKey);
  if (prevDepsKey !== depsKey) {
    setPrevDepsKey(depsKey);
    setPages(null);
  }
  const [fontsReady, setFontsReady] = useState<boolean>(() => {
    if (typeof document === 'undefined' || !('fonts' in document)) return true;
    return document.fonts.status === 'loaded';
  });
  // 計測済みの (depsKey, fontsReady) を保持し、同一なら再計測しない。
  const measuredFor = useRef<{ depsKey: unknown; fontsReady: boolean } | null>(null);

  // フォントロード完了を待ってから計測フラグを立てる（解決済みなら即 true）。
  useEffect(() => {
    if (fontsReady) return undefined;
    if (typeof document === 'undefined' || !('fonts' in document)) {
      setFontsReady(true);
      return undefined;
    }
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (!cancelled) setFontsReady(true);
    }).catch(() => {
      // フォント API が失敗しても計測自体は進める。
      if (!cancelled) setFontsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [fontsReady]);

  // depsKey 変化を含む再計測は、下の useLayoutEffect が measuredFor ガードで判定する。
  // （ここで setPages(null) すると確定直後に打ち消され、依存が参照安定なため再実行されず
  //   永久ブランクになる。reset 用 useEffect は置かない。オフスクリーンは常時描画し ref を新鮮に保つ）
  useLayoutEffect(() => {
    if (!fontsReady) return;
    // (depsKey, fontsReady) が前回と同一なら再計測しない（ループ防止）。
    const prev = measuredFor.current;
    if (prev && prev.depsKey === depsKey && prev.fontsReady === fontsReady) return;

    const blocks = blockRefs.current ?? [];
    // マウント途中で ref がまだ埋まっていない（null が残っている）うちは計測しない。
    // blockCount を超える末尾スロット（前回の大きい描画の残骸）は判定対象外にする。
    if (blocks.length < blockCount || blocks.slice(0, blockCount).some((b) => b === null)) return;

    const headerH = headerRef.current?.getBoundingClientRect().height ?? 0;
    const theadH = theadRef.current?.getBoundingClientRect().height ?? 0;
    // getBoundingClientRect().height は margin を含まないため、フッタの margin-top を加えて
    // 実際の占有高を予約する（過小予約だとフッタが次ページへ押し出される一因になる）。
    const footerEl = footerRef.current;
    const footerMarginTop = footerEl ? parseFloat(window.getComputedStyle(footerEl).marginTop || '0') : 0;
    const footerH = (footerEl?.getBoundingClientRect().height ?? 0) + (Number.isFinite(footerMarginTop) ? footerMarginTop : 0);
    // ブロック高さは計測テーブル内の各 <tr>/<tbody> を直接測る（thead を含まない）。
    const heights = Array.from(
      { length: blockCount },
      (_, i) => blocks[i]?.getBoundingClientRect().height ?? 0,
    );
    // 確定ページは [.page padding ＋ ヘッダー ＋ thead(1回) ＋ ブロック群 ＋ フッタ] なので、
    // ヘッダー高・thead 高・フッタ高（実測）・.page 上下 padding を引いた残りがブロックの利用可能高。
    // 安全マージン: 計測(画面)と印刷でのわずかな行高差や sub-pixel 累積でページをわずかに超え、
    // 末尾のフッタ（発行日時）だけが次ページへ押し出されることがある。約4mm分余裕を持たせて防ぐ。
    const SAFETY_MARGIN_PX = 16;
    const available = pageHeightPx - headerH - theadH - footerH - pageVerticalPaddingPx - SAFETY_MARGIN_PX;

    measuredFor.current = { depsKey, fontsReady };
    setPages(withPageNumbers(paginateByHeights(heights, available, keepWithNext))); // ← 1 回で確定
  }, [
    depsKey, fontsReady, blockCount, containerRef, blockRefs, headerRef, theadRef,
    footerRef, pageHeightPx, pageVerticalPaddingPx, keepWithNext,
  ]);

  return pages;
}
